import WindowsReleaseActions, { DownloadPlatformPicker, useDownloadPlatform } from "./WindowsReleaseActions";
import ProductDeviceActivation from "./ProductDeviceActivation";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, ShieldCheck } from "lucide-react";
import { useSecureMacProAccount } from "@/context/SecureMacProAccountContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { fortressOwnership } from "@/lib/fortressOwnership";
import { useSecureMacProStorefront } from "@/hooks/useSecureMacProStorefront";
import { useFortressStorefront } from "@/hooks/useFortressStorefront";
import { FORTRESS_PRO_ADDON_PRICE } from "@/config/fortressStorefront";
import {
  consumerErrorMessage,
  downloadSecureMacProRelease,
  startSecureMacProCheckout,
  startFortressProAddonCheckout,
} from "@/lib/consumerCommerce";

const PURCHASE_CONFIRM_ATTEMPTS = 10;
const PURCHASE_CONFIRM_INTERVAL_MS = 2000;
const SUPPORT_EMAIL = "contact@lightningflow.ai";

function EntitlementNotice({ status }) {
  if (status === "revoked") {
    return (
      <p className="text-sm text-ink-700" data-testid="pro-status-revoked">
        This SecureShield Pro licence is no longer active because the payment was refunded or reversed. SecureShield Pro stops
        receiving updates and new activations. If you think this is a mistake, email{" "}
        <a className="lf-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    );
  }
  if (status === "expired") {
    return (
      <p className="text-sm text-ink-700" data-testid="pro-status-expired">
        This SecureShield Pro licence has expired. Buy again to keep receiving updates.
      </p>
    );
  }
  return null;
}

function ProOwnedActions() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const download = useCallback(async () => {
    setError("");
    setBusy(true);
    try {
      const data = await downloadSecureMacProRelease();
      window.location.assign(data.download_url);
    } catch (err) {
      setError(consumerErrorMessage(err, "The download couldn't be prepared. Please try again."));
    } finally {
      setBusy(false);
    }
  }, []);
  return (
    <div className="space-y-3">
      <button className="lf-btn-primary" type="button" onClick={download} disabled={busy} data-testid="pro-download">
        <Download className="h-4 w-4" /> {busy ? "Preparing secure download…" : "Download SecureShield Pro"}
      </button>
      <p className="text-xs text-ink-500">
        Activate SecureShield Pro on up to 3 computers with this account. Download links expire after 5 minutes.
      </p>
      {error ? <p className="text-sm text-red-600" role="alert" data-testid="pro-download-error">{error}</p> : null}
    </div>
  );
}

function ProBuyAction({ storefront, catalogError, haloSubscriber, haloEntitlement, fortressCatalog }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const buy = useCallback(async () => {
    setError("");
    setBusy(true);
    try {
      const { url } = haloSubscriber
        ? await startFortressProAddonCheckout({ catalog: fortressCatalog, haloEntitlement })
        : await startSecureMacProCheckout();
      window.location.assign(url);
    } catch (err) {
      setError(err?.name === "ConsumerSaleClosedError"
        ? "Your Halo saving isn't ready yet. Please check back before buying."
        : consumerErrorMessage(err, "Checkout couldn't start. You haven't been charged."));
      setBusy(false);
    }
  }, [haloSubscriber, haloEntitlement, fortressCatalog]);
  if (!storefront.checkout_enabled) {
    return (
      <p className="text-sm text-ink-500" data-testid="pro-not-on-sale">
        {catalogError
          ? "We couldn't load SecureShield Pro pricing. Refresh to try again."
          : "SecureShield Pro isn't on sale yet."}
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <button className="lf-btn-primary" type="button" onClick={buy} disabled={busy} data-testid="pro-buy">
        {busy ? "Starting secure checkout…" : `Buy SecureShield Pro · ${haloSubscriber ? FORTRESS_PRO_ADDON_PRICE.label : storefront.price_usd}`} <ArrowUpRight className="h-4 w-4" />
      </button>
      <p className="text-xs text-ink-500">One-time purchase through Stripe. No subscription.{haloSubscriber ? " Your 40% saving is included at checkout." : ""}</p>
      {error ? <p className="text-sm text-red-600" role="alert" data-testid="pro-buy-error">{error}</p> : null}
    </div>
  );
}

function formatPurchaseAmount(amount, currency) {
  if (!Number.isInteger(amount) || !currency) return "—";
  if (currency.toLowerCase() === "aud") return `A$${(amount / 100).toFixed(2)}`;
  if (currency.toLowerCase() === "usd") return `US$${(amount / 100).toFixed(2)}`;
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

function ProPurchaseHistory({ purchases, error }) {
  if (error) {
    return <p className="text-sm text-red-600">SecureShield Pro purchase history is temporarily unavailable.</p>;
  }
  if (!purchases.length) return null;
  return (
    <section className="border-t border-ink-100 pt-5" data-testid="pro-purchase-history">
      <h3 className="font-display text-lg font-bold">Purchase history</h3>
      <div className="mt-3 space-y-3">
        {purchases.map((purchase, index) => (
          <div key={`${purchase.product}-${purchase.purchased_at || index}`} className="flex flex-wrap justify-between gap-3 text-sm">
            <div>
              <div className="font-semibold text-ink-900">SecureShield Pro</div>
              <div className="text-ink-500">Direct purchase · Stripe</div>
            </div>
            <div className="text-right text-ink-600">
              <div>{formatPurchaseAmount(purchase.amount_total, purchase.currency)}</div>
              <div>{purchase.purchased_at ? new Date(purchase.purchased_at).toLocaleDateString() : "—"}</div>
              <div className="capitalize">{purchase.status.replaceAll("_", " ")}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function LicensedMacs({ devices = [], error, releaseDevice, productName = "SecureShield Pro", testIdPrefix = "pro" }) {
  const [busy, setBusy] = useState("");
  const [releaseError, setReleaseError] = useState("");
  const release = useCallback(
    async (device) => {
      if (!window.confirm(`Release this computer from your ${productName} licence?`)) return;
      setBusy(device.device_id);
      setReleaseError("");
      try {
        await releaseDevice(device.device_id);
      } catch (err) {
        setReleaseError(consumerErrorMessage(err, "This computer couldn't be released. Please try again."));
      } finally {
        setBusy("");
      }
    },
    [releaseDevice, productName],
  );
  return (
    <section className="border-t border-ink-100 pt-5" data-testid={`${testIdPrefix}-devices`}>
      <h3 className="font-display text-lg font-bold">Licensed computers</h3>
      {error ? (
        <p className="mt-2 text-sm text-red-600">Device status is temporarily unavailable.</p>
      ) : devices.length ? (
        <div className="mt-3 divide-y divide-ink-100">
          {devices.map((device, index) => (
            <div key={device.device_id} className="flex items-center justify-between gap-4 py-3 text-sm">
              <div>
                <div className="font-semibold text-ink-900">Computer {index + 1}</div>
                <div className="text-ink-500">
                  {device.revoked ? "Released" : "Active"} · Activated {new Date(device.activated_at).toLocaleDateString()}
                </div>
              </div>
              {!device.revoked ? (
                <button
                  className="lf-btn-secondary"
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => release(device)}
                  data-testid={`${testIdPrefix}-release-device`}
                >
                  {busy === device.device_id ? "Releasing…" : "Release"}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-sm text-ink-500">No computers are using this licence yet.</p>
      )}
      <p className="mt-2 text-xs text-ink-500">Up to 3 active computers. Release one before activating a replacement.</p>
      {releaseError ? <p className="mt-2 text-sm text-red-600" role="alert">{releaseError}</p> : null}
    </section>
  );
}

export function usePurchaseConfirmation(purchaseState, ownership = null) {
  const account = useSecureMacProAccount();
  const { owned, refresh } = ownership || account;
  const [attempts, setAttempts] = useState(0);
  const timer = useRef(null);
  const confirming = purchaseState === "success" && !owned && attempts < PURCHASE_CONFIRM_ATTEMPTS;

  useEffect(() => {
    if (!confirming) return undefined;
    timer.current = setTimeout(async () => {
      await refresh();
      setAttempts((value) => value + 1);
    }, PURCHASE_CONFIRM_INTERVAL_MS);
    return () => clearTimeout(timer.current);
  }, [confirming, attempts, refresh]);

  if (purchaseState !== "success") return "";
  if (owned) return "confirmed";
  return confirming ? "confirming" : "delayed";
}

export function PurchaseBanner({ purchaseState, confirmation, productName = "SecureShield Pro", testIdPrefix = "pro" }) {
  if (purchaseState === "cancelled") {
    return <p className="border border-ink-100 bg-ink-50 p-4 text-sm text-ink-700" data-testid={`${testIdPrefix}-purchase-cancelled`}>Checkout was cancelled. You haven't been charged.</p>;
  }
  if (confirmation === "confirming") {
    return <p className="border border-electric/30 bg-electric/5 p-4 text-sm text-ink-700" role="status" data-testid={`${testIdPrefix}-purchase-confirming`}>{productName === "SecureShield Pro" ? "Payment received — confirming your licence. This usually takes a few seconds." : "Confirming your purchase. Access appears after payment verification."}</p>;
  }
  if (confirmation === "confirmed") {
    return <p className="border border-green-600/30 bg-green-50 p-4 text-sm text-green-800" role="status" data-testid={`${testIdPrefix}-purchase-confirmed`}>You're all set. {productName} is ready to download.</p>;
  }
  if (confirmation === "delayed") {
    return (
      <p className="border border-amber-500/40 bg-amber-50 p-4 text-sm text-ink-700" role="status" data-testid={`${testIdPrefix}-purchase-delayed`}>
        Your payment is still being confirmed. Refresh this page in a minute. If {productName} hasn't appeared within an
        hour, email <a className="lf-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> — you won't be charged twice.
      </p>
    );
  }
  return null;
}

export default function SecureMacProAccountPanel({ purchaseState = "" }) {
  const [platform, choosePlatform] = useDownloadPlatform();
  const { customer, entitlements: websiteEntitlements } = useCustomerAuth();
  const {
    account,
    accountError,
    entitlement,
    entitlementError,
    haloEntitlement,
    devices,
    devicesError,
    purchases,
    purchasesError,
    ready,
    owned,
    releaseDevice,
  } = useSecureMacProAccount();
  const { storefront, error: catalogError } = useSecureMacProStorefront();
  const { storefront: fortressStorefront, catalog: fortressCatalog } = useFortressStorefront();
  const confirmation = usePurchaseConfirmation(account ? purchaseState : "");
  const ownership = fortressOwnership({
    customerId: customer?.id,
    websiteEntitlements,
    consumerAccount: account,
    consumerEntitlement: entitlement,
    consumerHaloEntitlement: haloEntitlement,
    consumerReady: ready,
    consumerAccountError: accountError,
    consumerEntitlementError: entitlementError,
  });
  const haloSavingPending = ownership.halo
    && (ownership.haloPriceLinkPending || !fortressStorefront.pro.purchasable);

  return (
    <section className="mx-auto max-w-2xl border border-ink-100 bg-white p-7 md:p-9" aria-labelledby="securemac-pro-account-heading" data-testid="securemac-pro-account">
      <div className="flex items-start justify-between gap-4">
        <div>
          <ShieldCheck className="h-6 w-6 text-electric" strokeWidth={1.7} />
          <div className="lf-eyebrow mt-4">SecureShield Pro account</div>
          <h2 id="securemac-pro-account-heading" className="mt-2 font-display text-2xl font-bold">SecureShield Pro</h2>
          {account ? <p className="mt-1 text-sm text-ink-500" data-testid="pro-account-email">{account.email}</p> : null}
        </div>
      </div>

      {!ready ? (
        <p className="mt-6 text-sm text-ink-500" data-testid="pro-loading">Loading…</p>
      ) : !account ? (
        <p className="mt-6 text-sm text-ink-600" data-testid="pro-canonical-sign-in-required">
          Sign in to your LightningFlow account to manage SecureShield Pro.
        </p>
      ) : (
        <div className="mt-6 space-y-5">
          {accountError ? (
            <p className="text-sm text-red-600" role="alert" data-testid="pro-account-error">
              SecureShield Pro account services are temporarily unavailable. Your LightningFlow account and purchases are safe.
            </p>
          ) : (
            <>
              <PurchaseBanner purchaseState={purchaseState} confirmation={confirmation} />
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500" data-testid="pro-status">
                {entitlementError ? "Status unavailable" : owned ? "Purchased · Licence active" : entitlement?.status === "revoked" ? "Refunded or reversed" : entitlement?.status === "expired" ? "Expired" : "Not purchased yet"}
              </div>
            </>
          )}
          {accountError ? null : <DownloadPlatformPicker productName="SecureShield Pro" platform={platform} onChange={choosePlatform} />}
          {accountError ? null : entitlementError ? (
            <p className="text-sm text-red-600" role="alert" data-testid="pro-entitlement-error">
              We couldn't load your licence status. Refresh the page to try again.
            </p>
          ) : platform === "windows" ? (
            <WindowsReleaseActions product="securemac-pro" owned={owned} haloSubscriber={ownership.halo} haloEntitlement={haloEntitlement} catalog={fortressCatalog} savingPending={haloSavingPending} />
          ) : owned ? (
            <ProOwnedActions />
          ) : (
            <>
              <EntitlementNotice status={entitlement?.status} />
              {haloSavingPending ? (
                <p className="text-sm text-ink-700" role="status" data-testid="pro-halo-saving-pending">
                  Your Halo SuperVPN subscription is active. We're preparing your 40% saving on SecureShield Pro.
                  Please check back here before buying.
                </p>
              ) : confirmation === "confirming" ? null : <ProBuyAction
                storefront={storefront}
                catalogError={catalogError}
                haloSubscriber={ownership.halo}
                haloEntitlement={haloEntitlement}
                fortressCatalog={fortressCatalog}
              />}
            </>
          )}
          {accountError ? null : (
            <>
              <ProPurchaseHistory purchases={purchases.filter((purchase) => purchase.product === "securemac-pro")} error={purchasesError} />
              <LicensedMacs devices={devices} error={devicesError} releaseDevice={releaseDevice} />
              {owned && !entitlementError ? <ProductDeviceActivation product="securemac-pro" productName="SecureShield Pro" devices={devices} devicesError={devicesError} /> : null}
            </>
          )}
          <p className="text-xs text-ink-500">
            <Link className="lf-link" to="/secureshield/refunds">Refunds</Link> ·{" "}
            <Link className="lf-link" to="/secureshield/support">Support</Link> ·{" "}
            <Link className="lf-link" to="/secureshield/account-deletion">Account privacy &amp; deletion</Link>
          </p>
        </div>
      )}
    </section>
  );
}
